import React, { useEffect, useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { Link } from 'react-router-dom';
import { wyscoutMatchesAPI, fixturesAPI } from '@/services/api';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Fixture } from '@/types';

interface StoredMatch {
  id: number;
  wyscoutMatchId: number;
  dateUtc: string;
  status: string;
  venue: string;
  label: string;
  homeTeamId: number;
  homeScore: number;
  awayTeamId: number;
  awayScore: number;
  competitionId: number;
  seasonId: number;
}

const RecentMatchesWidget: React.FC = () => {
  const { currentWorkspace, hasWyscout } = useWorkspace();
  const [matches, setMatches] = useState<StoredMatch[]>([]);
  const [fixtures, setFixtures] = useState<Fixture[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ourTeamId, setOurTeamId] = useState<number | null>(null);

  const fetchMatches = async () => {
    setLoading(true);
    try {
        if (hasWyscout) {
          const [matchesRes, teamRes] = await Promise.all([
            wyscoutMatchesAPI.getStoredMatches(),
            wyscoutMatchesAPI.getTeamInfo().catch(() => null),
          ]);
          const played = (matchesRes.data || [])
            .filter((m: StoredMatch) => m.status === 'Played')
            .sort((a: StoredMatch, b: StoredMatch) => new Date(b.dateUtc).getTime() - new Date(a.dateUtc).getTime())
            .slice(0, 5);
          setMatches(played);
          setFixtures([]);

          if (teamRes?.data?.teamId) {
            setOurTeamId(teamRes.data.teamId);
          }
        } else {
          const fixturesRes = await fixturesAPI.list({ status: 'finished', limit: 5 });
          const initialFixtures = fixturesRes.data || [];
          let candidates = initialFixtures;

          if (fixturesRes.totalPages > 1) {
            const initialLimit = fixturesRes.limit;
            const requiredStart = Math.max(initialLimit + 1, fixturesRes.total - initialLimit + 1);
            let finalLimit = initialLimit;
            let finalPage = Math.ceil(fixturesRes.total / finalLimit);

            // Increase the page size only until the final page covers every newest-five candidate.
            while ((finalPage - 1) * finalLimit + 1 > requiredStart) {
              finalLimit += 1;
              finalPage = Math.ceil(fixturesRes.total / finalLimit);
            }

            const finalFixturesRes = await fixturesAPI.list({
              status: 'finished',
              page: finalPage,
              limit: finalLimit,
            });
            candidates = [...initialFixtures, ...(finalFixturesRes.data || [])];
          }

          setFixtures(candidates
            .sort((a, b) => new Date(b.kickoffAt).getTime() - new Date(a.kickoffAt).getTime())
            .slice(0, 5));
          setMatches([]);
          setOurTeamId(null);
        }
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load recent matches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchMatches();
    // Workspace capabilities are the request context triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, hasWyscout]);

  const parseTeams = (label: string) => {
    // label format: "Home Team - Away Team, Score1 - Score2"
    const parts = label.split(',');
    const teamsPart = parts[0] || '';
    const dashIdx = teamsPart.indexOf(' - ');
    if (dashIdx === -1) return { home: teamsPart.trim(), away: '' };
    return {
      home: teamsPart.slice(0, dashIdx).trim(),
      away: teamsPart.slice(dashIdx + 3).trim(),
    };
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const getResultColor = (match: StoredMatch) => {
    let ourScore: number;
    let theirScore: number;

    if (ourTeamId !== null) {
      // Use team ID to determine our result
      if (match.homeTeamId === ourTeamId) {
        ourScore = match.homeScore;
        theirScore = match.awayScore;
      } else if (match.awayTeamId === ourTeamId) {
        ourScore = match.awayScore;
        theirScore = match.homeScore;
      } else {
        // Neither team is ours — neutral
        return 'text-gray-600 bg-gray-50 dark:bg-gray-700/50';
      }
    } else {
      // Fallback: assume home team perspective
      ourScore = match.homeScore;
      theirScore = match.awayScore;
    }

    if (ourScore > theirScore) return 'text-green-600 bg-green-50 dark:bg-green-900/20';
    if (ourScore < theirScore) return 'text-red-600 bg-red-50 dark:bg-red-900/20';
    return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
  };

  const title = 'Recent matches';

  if (loading) {
    return (
      <Card className="h-full">
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
          <div
            role="status"
            aria-live="polite"
            aria-label="Loading recent matches"
            className="animate-pulse space-y-3 motion-reduce:animate-none motion-reduce:transition-none"
          >
            <span className="sr-only">Loading recent matches</span>
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-full">
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
          <p className="mb-3 text-sm text-red-600 dark:text-red-300">{error}</p>
          <Button variant="outline" size="sm" className="min-h-11 motion-reduce:transition-none" onClick={() => void fetchMatches()}>
            Retry recent matches
          </Button>
        </div>
      </Card>
    );
  }

  // Fixtures view (non-Wyscout workspaces)
  if (!hasWyscout) {
    return (
      <Card className="h-full">
        <div className="p-4 h-full flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            <Link to="/matches" className="inline-flex min-h-11 items-center rounded-md px-3 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 motion-reduce:transition-none">
              View All
            </Link>
          </div>

          {fixtures.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No completed matches found</p>
          ) : (
            <div className="space-y-2 flex-1">
              {fixtures.map((fixture) => {
                const matchDate = formatDate(fixture.kickoffAt);
                return (
                  <Link
                    key={fixture.id}
                    to={`/matches/${fixture.id}`}
                    aria-label={`Completed match on ${matchDate}`}
                    className="flex min-h-11 items-center justify-between rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-gray-800 dark:hover:bg-gray-700 motion-reduce:transition-none"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                        {fixture.teamName || 'Completed match'}
                      </span>
                      <span className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <time dateTime={fixture.kickoffAt}>{matchDate}</time>
                        <span>{fixture.venue.charAt(0).toUpperCase() + fixture.venue.slice(1)}</span>
                        <span>Finished</span>
                      </span>
                    </span>
                    <svg aria-hidden="true" className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Wyscout matches view (current behavior)
  return (
    <Card className="h-full">
      <div className="p-4 h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <Link to="/matches" className="inline-flex min-h-11 items-center rounded-md px-3 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 motion-reduce:transition-none">
            View All
          </Link>
        </div>

        {matches.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No recent matches found</p>
        ) : (
          <div className="space-y-2 flex-1">
            {matches.map((match) => {
              const teams = parseTeams(match.label);
              return (
                <Link
                  key={match.wyscoutMatchId}
                  to={`/matches/wyscout/${match.wyscoutMatchId}`}
                  aria-label={`${teams.home} ${match.homeScore} - ${match.awayScore} ${teams.away}, ${formatDate(match.dateUtc)}`}
                  className="flex min-h-11 items-center justify-between rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-gray-800 dark:hover:bg-gray-700 motion-reduce:transition-none"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 truncate text-right">
                        {teams.home}
                      </span>
                      <span className={`shrink-0 text-sm font-bold px-2 py-0.5 rounded ${getResultColor(match)}`}>
                        {match.homeScore} - {match.awayScore}
                      </span>
                      <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {teams.away}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {formatDate(match.dateUtc)}
                    </p>
                  </div>
                  <svg aria-hidden="true" className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Card>
  );
};

export default RecentMatchesWidget;
